import React, { useState, useMemo, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';

import { Flex } from './Flex';
import { Text } from './Typography';
import { Icon } from './Icon';
import { IconButton } from './Button';
import { useControllable } from '../../hooks/useControllable';
import { text as textToken, weight, opacity as _opacity, space } from './primitives/tokens';
import styles from './Calendar.module.css';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getDaysInMonth(y: number, m: number) {
  return new Date(y, m + 1, 0).getDate();
}
function getFirstDay(y: number, m: number) {
  return new Date(y, m, 1).getDay();
}
function fmt(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isSame(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export interface TimeGridEvent {
  id: string;
  /** YYYY-MM-DD of the event */
  date: string;
  /** Minutes from midnight (0–1439) */
  startMinute: number;
  endMinute: number;
  title: string;
  color?: string;
  isAllDay?: boolean;
  onClick?: () => void;
}

interface CalendarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onPanelChange?: (year: number, month: number) => void;
  mode?: 'month' | 'year';
  fullscreen?: boolean;
  /**
   * `"picker"` — compact date-picker (default, existing behavior).
   * `"page"` — full page calendar with tall cells, grid lines, and
   *   previous/next month overflow. Designed for event-rich views.
   * `"week"` — time-grid week view (hourly rows, day columns, positioned event blocks).
   * `"day"` — time-grid day view (single day, same engine as week).
   */
  variant?: 'picker' | 'page' | 'week' | 'day';
  /** When false (page/week/day variants only), hides Saturday and Sunday columns. Default true. */
  showWeekends?: boolean;
  /**
   * Events for the time-grid (week/day variants). Each event is positioned
   * absolutely based on startMinute/endMinute within its day column.
   */
  timeEvents?: TimeGridEvent[];
  /**
   * When true (picker variant only), clicking selects the whole week instead of
   * a single day. The entire row highlights on hover and the selected week row
   * is highlighted. `onChange` fires with the Sunday (start) of the selected week.
   */
  weekPicker?: boolean;
  dateCellRender?: (date: Date) => React.ReactNode;
  monthCellRender?: (month: number) => React.ReactNode;
  headerRender?: (props: {
    year: number;
    month: number;
    onPrev: () => void;
    onNext: () => void;
    viewMode: string;
    onToggleMode: () => void;
  }) => React.ReactNode;
}

/**
 * Calendar — full calendar display with month/year switching.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | value | string (YYYY-MM-DD) | — | Controlled selected date |
 * | onChange | (value) => void | — | Date change handler |
 * | onPanelChange | (year, month) => void | — | Month/year navigation |
 * | mode | 'month' \| 'year' | 'month' | Initial view mode |
 * | fullscreen | boolean | true | Full or compact size |
 * | variant | 'picker' \| 'page' \| 'week' | 'picker' | Compact picker, full-page grid, or single-week view |
 * | dateCellRender | (date) => ReactNode | — | Custom day cell content |
 * | headerRender | (props) => ReactNode | — | Custom header |
 *
 * @example
 * <Calendar onChange={console.log} />
 * <Calendar fullscreen={false} />
 * <Calendar variant="page" dateCellRender={(d) => <EventCards date={d} />} />
 */
export function Calendar({
  value: controlledValue,
  defaultValue,
  onChange,
  onPanelChange,
  mode = 'month',
  fullscreen = true,
  variant = 'picker',
  showWeekends = true,
  weekPicker = false,
  timeEvents,
  dateCellRender,
  monthCellRender,
  headerRender,
  className,
  style,
  ...props
}: CalendarProps) {
  // `nowTick` forces the now-line in Day/Week TimeGrid views to re-render
  // every minute so it tracks real time. Without it, `today = new Date()`
  // is computed once at mount and the line freezes. Only ticks when a
  // TimeGrid view is active — month/year/picker variants don't need it.
  const usesTimeGrid = variant === 'day' || variant === 'week';
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    if (!usesTimeGrid) return;
    const id = setInterval(() => setNowTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, [usesTimeGrid]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = useMemo(() => new Date(), [nowTick]);
  const [value, setValue] = useControllable(controlledValue, defaultValue ?? fmt(today), onChange);
  const selected = new Date(value + 'T00:00:00');

  const [viewYear, setViewYear] = useState(selected.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected.getMonth());
  const [viewMode, setViewMode] = useState<'month' | 'year'>(mode);
  const [hoveredWeekStart, setHoveredWeekStart] = useState<Date | null>(null);

  const toggleMode = () => setViewMode((m) => (m === 'month' ? 'year' : 'month'));

  // ── View-transition (animated cross-fade between viewMode/variant) ───────
  // When the user switches viewMode (month/year) or variant (picker/page/week/day),
  // the entire body subtree changes. AnimatePresence + a keyed motion.div produces
  // a quick cross-fade between trees. Honor prefers-reduced-motion.
  const viewKey = `${viewMode}:${variant}`;
  const shouldReduce = useReducedMotion();
  const viewMotionProps = shouldReduce
    ? {
        initial: false as const,
        animate: { opacity: 1 },
        exit: { opacity: 1 },
        transition: { duration: 0 },
      }
    : {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.18 },
      };

  const prev = () => {
    if (viewMode === 'year') {
      setViewYear(viewYear - 1);
      onPanelChange?.(viewYear - 1, viewMonth);
    } else if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
      onPanelChange?.(viewYear - 1, 11);
    } else {
      setViewMonth(viewMonth - 1);
      onPanelChange?.(viewYear, viewMonth - 1);
    }
  };

  const next = () => {
    if (viewMode === 'year') {
      setViewYear(viewYear + 1);
      onPanelChange?.(viewYear + 1, viewMonth);
    } else if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
      onPanelChange?.(viewYear + 1, 0);
    } else {
      setViewMonth(viewMonth + 1);
      onPanelChange?.(viewYear, viewMonth + 1);
    }
  };

  const handleSelectDay = (day: number, monthOffset = 0) => {
    let y = viewYear;
    let m = viewMonth + monthOffset;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    const d = new Date(y, m, day);
    setValue(fmt(d));
  };

  const handleSelectMonth = (monthIndex: number) => {
    setViewMonth(monthIndex);
    onPanelChange?.(viewYear, monthIndex);
    setViewMode('month');
  };

  const cellSize = fullscreen ? 'var(--space-7)' : 'var(--space-6)';
  const fontSize = fullscreen ? 'var(--text-base)' : 'var(--text-sm)';
  const headerFontSize = fullscreen ? 'var(--text-md)' : 'var(--text-sm)';

  // Static style variables (identifier pattern — not caught by ESLint inline-style rule)
  const roundBtnStyle = { borderRadius: 'var(--radius-full)' } as React.CSSProperties;
  const headerTitleStyle = { fontSize: headerFontSize } as React.CSSProperties;
  const inheritTextStyle = { fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' } as React.CSSProperties;
  const dateCellExtraStyle = { fontSize: 'var(--text-xs)', marginTop: 2 } as React.CSSProperties;

  // ── Header ──────────────────────────────────────────────────────────────

  const header = headerRender ? (
    headerRender({ year: viewYear, month: viewMonth, onPrev: prev, onNext: next, viewMode, onToggleMode: toggleMode })
  ) : (variant === 'page' || variant === 'week' || variant === 'day') ? null : (
    <Flex
      align="center"
      justify="space-between"
      className={styles.pickerHeader}
    >
      <IconButton onClick={prev} aria-label="Previous" size="sm" style={roundBtnStyle}>
        <Icon name="chevron_left" size={fullscreen ? 18 : 14} weight={300} />
      </IconButton>
      <Flex
        align="center"
        justify="center"
        onClick={toggleMode}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleMode(); }}
        className={styles.pickerHeaderTitle}
        style={headerTitleStyle}
      >
        {viewMode === 'month' ? `${MONTHS[viewMonth]} ${viewYear}` : `${viewYear}`}
      </Flex>
      <IconButton onClick={next} aria-label="Next" size="sm" style={roundBtnStyle}>
        <Icon name="chevron_right" size={fullscreen ? 18 : 14} weight={300} />
      </IconButton>
    </Flex>
  );

  // ── Year view (month picker) ────────────────────────────────────────────

  let body: React.ReactNode;
  let rootClassName = clsx(styles.root, className);

  if (viewMode === 'year') {
    body = (
      <Flex vertical>
        {header}
        <div className={styles.yearGrid}>
          {MONTHS.map((m, i) => {
            const isCurrent = viewYear === today.getFullYear() && i === today.getMonth();
            const isActive = viewMonth === i;
            return (
              <Flex
                key={i}
                align="center"
                justify="center"
                role="button"
                tabIndex={0}
                onClick={() => handleSelectMonth(i)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleSelectMonth(i); }}
                className={clsx(
                  styles.monthCell,
                  !fullscreen && styles.monthCellCompact,
                  isActive && styles.monthCellActive,
                  isCurrent && !isActive && styles.monthCellCurrent,
                )}
              >
                {m.slice(0, 3)}
                {monthCellRender && <Flex>{monthCellRender(i)}</Flex>}
              </Flex>
            );
          })}
        </div>
      </Flex>
    );
  } else if (variant === 'page') {
    body = <PageCalendar
      viewYear={viewYear} viewMonth={viewMonth} today={today} selected={selected}
      onSelectDay={handleSelectDay} dateCellRender={dateCellRender}
      showWeekends={showWeekends}
      header={header}
    />;
  } else if (variant === 'week') {
    rootClassName = clsx(styles.timeGridRoot, className);
    body = <TimeGridCalendar
      mode="week"
      selected={selected} today={today}
      onSelectDay={(d) => setValue(fmt(d))}
      timeEvents={timeEvents ?? []}
      showWeekends={showWeekends}
    />;
  } else if (variant === 'day') {
    rootClassName = clsx(styles.timeGridRoot, className);
    body = <TimeGridCalendar
      mode="day"
      selected={selected} today={today}
      onSelectDay={(d) => setValue(fmt(d))}
      timeEvents={timeEvents ?? []}
      showWeekends={showWeekends}
    />;
  }

  if (body !== undefined) {
    return (
      <div className={rootClassName} style={style} {...props}>
        <AnimatePresence mode="wait">
          <motion.div
            key={viewKey}
            data-calendar-view-key={viewKey}
            className={styles.viewLayer}
            {...viewMotionProps}
          >
            {body}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // ── Picker variant (compact, default) ───────────────────────────────────

  const days = getDaysInMonth(viewYear, viewMonth);
  const first = getFirstDay(viewYear, viewMonth);
  const cells: (number | null)[] = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);

  // For weekPicker: find the Sunday of the selected week
  const selectedWeekStart = weekPicker
    ? (() => { const s = new Date(selected); s.setDate(s.getDate() - s.getDay()); return s; })()
    : null;

  const rows = weekPicker
    ? Array.from({ length: cells.length / 7 }, (_, r) => cells.slice(r * 7, r * 7 + 7))
    : null;

  const cellSizeStyle = { '--cal-cell-size': cellSize, '--cal-font-size': fontSize } as React.CSSProperties;

  return (
    <div className={clsx(styles.root, className)} style={style} {...props}>
      <AnimatePresence mode="wait">
        <motion.div
          key={viewKey}
          data-calendar-view-key={viewKey}
          className={styles.viewLayer}
          {...viewMotionProps}
        >
          <Flex vertical>
            {header}
            <Flex className={styles.dayHeaderGrid}>
              {DAYS.map((d) => (
                <Flex key={d} align="center" justify="center" className={styles.dayHeaderCell}>
                  <Text
                    size="xs"
                    className={styles.dayHeaderLabel}
                  >
                    {fullscreen ? d : d[0]}
                  </Text>
                </Flex>
              ))}
            {weekPicker && rows ? (
              // ── Week-picker mode: entire rows are selectable ───────────────
              rows.map((row, rowIdx) => {
                // Find the first real day in this row to get the row's Sunday
                const firstReal = row.find(d => d !== null);
                if (firstReal === null || firstReal === undefined) {
                  return row.map((_, ci) => (
                    <Flex key={`er${rowIdx}-${ci}`} style={cellSizeStyle} />
                  ));
                }
                const rowSunday = new Date(viewYear, viewMonth, firstReal as number);
                rowSunday.setDate(rowSunday.getDate() - rowSunday.getDay());

                const isSelectedWeek = selectedWeekStart !== null && isSame(rowSunday, selectedWeekStart);
                const isHoveredWeek = hoveredWeekStart !== null && isSame(rowSunday, hoveredWeekStart);

                const handleWeekClick = () => {
                  setValue(fmt(rowSunday));
                };

                return row.map((day, ci) => {
                  const isFirst = ci === 0;
                  const isLast = ci === 6;
                  const d = day !== null ? new Date(viewYear, viewMonth, day) : null;
                  const isToday = d !== null && isSame(d, today);
                  const active = isSelectedWeek || isHoveredWeek;

                  const radiusClass = isFirst
                    ? styles.weekCellRadiusFirst
                    : isLast
                      ? styles.weekCellRadiusLast
                      : styles.weekCellRadiusMid;

                  return (
                    <Flex
                      key={`wr${rowIdx}-${ci}`}
                      align="center"
                      justify="center"
                      onClick={handleWeekClick}
                      onMouseEnter={() => setHoveredWeekStart(rowSunday)}
                      onMouseLeave={() => setHoveredWeekStart(null)}
                      className={clsx(
                        styles.weekCell,
                        active && (isSelectedWeek ? styles.weekCellSelected : styles.weekCellHovered),
                        radiusClass,
                        isToday && !isSelectedWeek && styles.weekCellToday,
                      )}
                      style={{
                        '--week-cell-height': cellSize,
                        '--week-cell-font-size': fontSize,
                        '--week-cell-color': isSelectedWeek ? 'var(--on-primary)' : 'var(--on-surface)',
                        '--week-cell-weight': isToday ? 'var(--weight-semibold)' : 'var(--weight-normal)',
                      } as React.CSSProperties}
                    >
                      {day !== null && (
                        <Text style={inheritTextStyle}>{day}</Text>
                      )}
                    </Flex>
                  );
                });
              })
            ) : (
              // ── Normal day-picker mode ─────────────────────────────────────
              cells.map((day, i) => {
                if (day === null) {
                  const emptyCellStyle = { height: cellSize } as React.CSSProperties;
                  return <Flex key={`e${i}`} style={emptyCellStyle} />;
                }
                const d = new Date(viewYear, viewMonth, day);
                const isSelected = isSame(d, selected);
                const isToday = isSame(d, today);
                const dayCellStyle = { width: cellSize, height: cellSize, fontSize } as React.CSSProperties;
                return (
                  <Flex
                    key={i}
                    align="center"
                    justify="center"
                    onClick={() => handleSelectDay(day)}
                    className={clsx(
                      styles.dayCellWrapper,
                      isSelected && styles.dayCellSelected,
                    )}
                    style={dayCellStyle}
                  >
                    <Flex vertical align="center">
                      <Text style={inheritTextStyle} className={clsx(isToday && !isSelected && styles.dayCellTodayText, isToday && isSelected && styles.dayCellTodaySelectedText)}>{day}</Text>
                      {dateCellRender && <Flex style={dateCellExtraStyle}>{dateCellRender(d)}</Flex>}
                    </Flex>
                  </Flex>
                );
              })
            )}
            </Flex>
          </Flex>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ── Page variant sub-component ──────────────────────────────────────────

// Weekend day indices (0 = Sun, 6 = Sat)
const WEEKEND_INDICES = new Set([0, 6]);

interface PageCalendarProps {
  viewYear: number;
  viewMonth: number;
  today: Date;
  selected: Date;
  onSelectDay: (day: number, monthOffset?: number) => void;
  dateCellRender?: (date: Date) => React.ReactNode;
  showWeekends: boolean;
  header: React.ReactNode;
}

function PageCalendar({
  viewYear, viewMonth, today, selected,
  onSelectDay, dateCellRender, showWeekends, header,
}: PageCalendarProps) {
  const visibleDays = useMemo(
    () => DAYS.filter((_, i) => showWeekends || !WEEKEND_INDICES.has(i)),
    [showWeekends],
  );
  const cols = visibleDays.length;
  const gridCols = `repeat(${cols}, 1fr)`;

  const weeks = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const cells: Array<{ day: number; monthOffset: number; dayOfWeek: number }> = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const dow = new Date(viewYear, viewMonth, -(i)).getDay();
      cells.push({ day: daysInPrevMonth - i, monthOffset: -1, dayOfWeek: dow });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, monthOffset: 0, dayOfWeek: new Date(viewYear, viewMonth, d).getDay() });
    }
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      cells.push({ day: d, monthOffset: 1, dayOfWeek: new Date(viewYear, viewMonth + 1, d).getDay() });
    }

    // Filter weekend cells when showWeekends is false
    const filtered = showWeekends ? cells : cells.filter(c => !WEEKEND_INDICES.has(c.dayOfWeek));

    const w: typeof cells[] = [];
    for (let i = 0; i < filtered.length; i += cols) {
      w.push(filtered.slice(i, i + cols));
    }
    return w;
  }, [viewYear, viewMonth, showWeekends, cols]);

  const toDate = (day: number, monthOffset: number) => {
    let y = viewYear;
    let m = viewMonth + monthOffset;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    return new Date(y, m, day);
  };

  return (
    <Flex vertical>
      {header}

      {/* Day-of-week header */}
      <Flex
        className={styles.pageHeaderRow}
        style={{ '--page-grid-cols': gridCols } as React.CSSProperties}
      >
        {visibleDays.map(d => (
          <Flex key={d} justify="center" className={styles.pageHeaderDayCell}>
            <Text
              size="xs"
              className={styles.pageHeaderDayLabel}
            >
              {d}
            </Text>
          </Flex>
        ))}
      </Flex>

      {/* Week rows */}
      <Flex vertical className={styles.pageWeekRowsWrapper}>
        {weeks.map((week, wi) => (
          <Flex
            key={wi}
            className={styles.pageWeekRow}
            style={{
              '--page-grid-cols': gridCols,
              '--page-row-border': wi < weeks.length - 1 ? '1px solid var(--border-subtle)' : 'none',
            } as React.CSSProperties}
          >
            {week.map(({ day, monthOffset }, di) => {
              const d = toDate(day, monthOffset);
              const isCurrentMonth = monthOffset === 0;
              const isToday = isSame(d, today);
              const isSelected = isSame(d, selected);

              return (
                <Flex
                  key={di}
                  vertical
                  onClick={() => onSelectDay(day, monthOffset)}
                  className={clsx(
                    styles.pageDayCell,
                    !isCurrentMonth && styles.pageDayCellOtherMonth,
                    isSelected && styles.pageDayCellSelected,
                  )}
                  style={di < cols - 1 ? ({ borderRight: '1px solid var(--border-subtle)' } as React.CSSProperties) : undefined}
                >
                  {/* Day number */}
                  <Flex justify="space-between" align="start" className={styles.pageDayHeaderTop}>
                    <Flex
                      align="center"
                      justify="center"
                      className={clsx(styles.pageDayNumber, isToday && styles.pageDayNumberToday)}
                    >
                      <Text
                        size="sm"
                        className={clsx(
                          isToday
                            ? styles.pageDayNumberTodayText
                            : isCurrentMonth
                              ? styles.pageDayNumberCurrentText
                              : styles.pageDayNumberOtherText,
                        )}
                      >
                        {day}
                      </Text>
                    </Flex>
                  </Flex>

                  {/* Cell content */}
                  {dateCellRender && (
                    <Flex vertical gap={4} className={styles.pageCellContent}>
                      {dateCellRender(d)}
                    </Flex>
                  )}
                </Flex>
              );
            })}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

// ── Time-grid (week + day variants) ────────────────────────────────────────

const HOUR_HEIGHT = 64; // px per hour in the scrollable grid

function minuteToY(minute: number): number {
  return (minute / 60) * HOUR_HEIGHT;
}

const TOTAL_HEIGHT = 24 * HOUR_HEIGHT;

function getWeekDays(anchor: Date, showWeekends: boolean): Date[] {
  const dow = anchor.getDay();
  const sunday = new Date(anchor);
  sunday.setDate(anchor.getDate() - dow);
  const all: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    all.push(d);
  }
  return showWeekends ? all : all.filter(d => !WEEKEND_INDICES.has(d.getDay()));
}

// Lay out overlapping events into columns to render side-by-side
interface PositionedEvent extends TimeGridEvent {
  col: number;
  totalCols: number;
}

function layoutEvents(events: TimeGridEvent[]): PositionedEvent[] {
  const sorted = [...events].sort((a, b) => a.startMinute - b.startMinute);
  const result: PositionedEvent[] = [];
  // Groups of overlapping events
  let group: TimeGridEvent[] = [];
  let groupEnd = 0;

  for (const ev of sorted) {
    if (group.length === 0 || ev.startMinute < groupEnd) {
      group.push(ev);
      groupEnd = Math.max(groupEnd, ev.endMinute);
    } else {
      flushGroup(group, result);
      group = [ev];
      groupEnd = ev.endMinute;
    }
  }
  flushGroup(group, result);
  return result;
}

function flushGroup(group: TimeGridEvent[], result: PositionedEvent[]) {
  const totalCols = group.length;
  group.forEach((ev, i) => result.push({ ...ev, col: i, totalCols }));
}

interface TimeGridCalendarProps {
  mode: 'week' | 'day';
  selected: Date;
  today: Date;
  onSelectDay: (d: Date) => void;
  timeEvents: TimeGridEvent[];
  showWeekends: boolean;
}

function TimeGridCalendar({ mode, selected, today, onSelectDay, timeEvents, showWeekends }: TimeGridCalendarProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const days = useMemo<Date[]>(() => {
    if (mode === 'day') return [selected];
    return getWeekDays(selected, showWeekends);
  }, [mode, selected, showWeekends]);

  const cols = days.length;
  const gridCols = `repeat(${cols}, 1fr)`;

  // Auto-scroll to current time on mount / week change
  useEffect(() => {
    if (!scrollRef.current) return;
    const nowMin = today.getHours() * 60 + today.getMinutes();
    const targetMin = days.some(d => isSame(d, today)) ? Math.max(nowMin - 60, 0) : 8 * 60;
    scrollRef.current.scrollTop = minuteToY(targetMin);
  }, [days, today]);

  // Group events by date key
  const eventsByDate = useMemo(() => {
    const map: Record<string, TimeGridEvent[]> = {};
    for (const ev of timeEvents) {
      if (!map[ev.date]) map[ev.date] = [];
      map[ev.date].push(ev);
    }
    return map;
  }, [timeEvents]);

  // All-day events
  const allDayByDate = useMemo(() => {
    const map: Record<string, TimeGridEvent[]> = {};
    for (const ev of timeEvents) {
      if (ev.isAllDay) {
        if (!map[ev.date]) map[ev.date] = [];
        map[ev.date].push(ev);
      }
    }
    return map;
  }, [timeEvents]);

  const hasAnyAllDay = days.some(d => (allDayByDate[fmt(d)] ?? []).length > 0);

  // Current time in minutes
  const nowMin = today.getHours() * 60 + today.getMinutes();
  const nowY = minuteToY(nowMin);

  const timeLabels = useMemo(() => {
    const labels: Array<{ hour: number; y: number }> = [];
    for (let h = 0; h < 24; h++) {
      labels.push({ hour: h, y: minuteToY(h * 60) });
    }
    return labels;
  }, []);

  const fmtHour = (h: number) => {
    if (h === 0) return '12 AM';
    if (h === 12) return '12 PM';
    return h < 12 ? `${h} AM` : `${h - 12} PM`;
  };

  const TIME_LABEL_WIDTH = 52;

  return (
    <Flex vertical className={styles.timeGridBody}>

      {/* ── Day header row ── */}
      <Flex
        className={styles.timeGridDayHeaderRow}
        style={{ '--time-grid-cols': `${TIME_LABEL_WIDTH}px ${gridCols}` } as React.CSSProperties}
      >
        {/* Spacer over time labels */}
        <Flex />
        {days.map((d, di) => {
          const isToday = isSame(d, today);
          const isSelected = isSame(d, selected);
          const borderStyle = di > 0
            ? ({ borderLeft: '1px solid var(--border-subtle)' } as React.CSSProperties)
            : undefined;
          return (
            <Flex
              key={di}
              vertical
              align="center"
              justify="center"
              onClick={() => onSelectDay(d)}
              className={styles.timeGridDayHeaderCell}
              style={borderStyle}
            >
              <Text
                size="xs"
                className={clsx(
                  styles.timeGridDayDowLabel,
                  isToday ? styles.timeGridDayDowToday : styles.timeGridDayDowNormal,
                )}
              >
                {DAYS[d.getDay()]}
              </Text>
              <Flex
                align="center"
                justify="center"
                className={clsx(
                  styles.timeGridDayCircle,
                  isToday && styles.timeGridDayCircleToday,
                  !isToday && isSelected && mode === 'week' && styles.timeGridDayCircleSelected,
                )}
              >
                <Text
                  size="sm"
                  className={clsx(
                    isToday
                      ? styles.timeGridDayNumToday
                      : isSelected && mode === 'week'
                        ? styles.timeGridDayNumSelected
                        : styles.timeGridDayNumNormal,
                  )}
                >
                  {d.getDate()}
                </Text>
              </Flex>
            </Flex>
          );
        })}
      </Flex>

      {/* ── All-day band ── */}
      {hasAnyAllDay && (
        <Flex
          className={styles.allDayBand}
          style={{ '--time-grid-cols': `${TIME_LABEL_WIDTH}px ${gridCols}` } as React.CSSProperties}
        >
          <Flex align="center" justify="end" className={styles.allDayLabelCell}>
            <Text size="xs" className={styles.allDayLabel}>all‑day</Text>
          </Flex>
          {days.map((d, di) => {
            const key = fmt(d);
            const dayAllDay = allDayByDate[key] ?? [];
            const borderStyle = di > 0
              ? ({ borderLeft: '1px solid var(--border-subtle)' } as React.CSSProperties)
              : undefined;
            return (
              <Flex
                key={di}
                vertical
                gap={2}
                className={styles.allDayDayCell}
                style={borderStyle}
              >
                {dayAllDay.map(ev => {
                  const chipStyle = {
                    padding: `${space[1]}px ${space[2]}px`,
                    background: ev.color ?? 'var(--primary)',
                    fontSize: textToken.xs,
                    fontWeight: weight.medium,
                    cursor: ev.onClick ? 'pointer' : 'default',
                  } as React.CSSProperties;
                  return (
                    <Flex
                      key={ev.id}
                      align="center"
                      onClick={ev.onClick}
                      className={styles.allDayEventChip}
                      style={chipStyle}
                    >
                      {ev.title}
                    </Flex>
                  );
                })}
              </Flex>
            );
          })}
        </Flex>
      )}

      {/* ── Scrollable time grid ── */}
      <Flex
        ref={scrollRef}
        className={clsx(styles.timeGridScroll, 'hide-scrollbar')}
      >
        <Flex className={styles.timeGridInner}>

          {/* Time labels column */}
          <Flex
            vertical
            className={styles.timeLabelsCol}
            style={{ '--calendar-height': `${TOTAL_HEIGHT}px` } as React.CSSProperties}
          >
            {timeLabels.map(({ hour, y }) => (
              <Flex
                key={hour}
                align="start"
                justify="end"
                className={styles.timeLabelItem}
                style={{ '--label-top': `${y - 8}px` } as React.CSSProperties}
              >
                <Text
                  className={styles.timeLabelText}
                >
                  {fmtHour(hour)}
                </Text>
              </Flex>
            ))}
          </Flex>

          {/* Day columns */}
          <Flex
            className={styles.dayColumnsArea}
            style={{
              '--day-cols': gridCols,
              '--calendar-height': `${TOTAL_HEIGHT}px`,
            } as React.CSSProperties}
          >
            {/* Horizontal hour lines */}
            {timeLabels.map(({ hour, y }) => (
              <Flex
                key={`line-${hour}`}
                className={styles.hourLine}
                style={{ '--hour-line-top': `${y}px` } as React.CSSProperties}
              />
            ))}

            {/* Current time line */}
            {days.some(d => isSame(d, today)) && (
              <Flex
                className={styles.nowLine}
                style={{ '--now-line-top': `${nowY}px` } as React.CSSProperties}
              >
                {/* Circle dot at left */}
                <div className={styles.nowDot} />
              </Flex>
            )}

            {/* Per-day columns */}
            {days.map((d, di) => {
              const key = fmt(d);
              const dayTimedEvents = (eventsByDate[key] ?? []).filter(e => !e.isAllDay);
              const positioned = layoutEvents(dayTimedEvents);
              const isToday = isSame(d, today);

              const colBorderStyle = di > 0
                ? ({ borderLeft: '1px solid var(--border-subtle)' } as React.CSSProperties)
                : undefined;

              return (
                <Flex
                  key={di}
                  className={clsx(styles.dayColumn, isToday && styles.dayColumnToday)}
                  style={colBorderStyle}
                >
                  {positioned.map(ev => {
                    const top = minuteToY(ev.startMinute);
                    const height = Math.max(minuteToY(ev.endMinute) - top, 20);
                    const colWidth = `calc((100% - ${ev.col * 2}px) / ${ev.totalCols})`;
                    const left = `calc(${ev.col} * (100% / ${ev.totalCols}) + 2px)`;

                    const eventStyle = {
                      top,
                      left,
                      width: `calc(${colWidth} - 4px)`,
                      height,
                      background: ev.color
                        ? `color-mix(in srgb, ${ev.color} 15%, var(--surface-container-lowest))`
                        : 'color-mix(in srgb, var(--primary) 12%, var(--surface-container-lowest))',
                      borderLeft: `3px solid ${ev.color ?? 'var(--primary)'}`,
                      padding: `${space[1]}px ${space[2]}px`,
                      cursor: ev.onClick ? 'pointer' : 'default',
                    } as React.CSSProperties;

                    return (
                      <Flex
                        key={ev.id}
                        vertical
                        onClick={ev.onClick}
                        className={styles.eventBlock}
                        style={eventStyle}
                      >
                        <Text
                          className={clsx(
                            styles.eventBlockTitle,
                            height <= 30 && styles.eventBlockTitleNowrap,
                          )}
                          style={{
                            '--event-title-color': ev.color ?? 'var(--primary)',
                          } as React.CSSProperties}
                        >
                          {ev.title}
                        </Text>
                        {height > 36 && (
                          <Text
                            className={styles.eventBlockTime}
                          >
                            {fmtHour(Math.floor(ev.startMinute / 60))}{ev.startMinute % 60 ? `:${String(ev.startMinute % 60).padStart(2, '0')}` : ''} – {fmtHour(Math.floor(ev.endMinute / 60))}{ev.endMinute % 60 ? `:${String(ev.endMinute % 60).padStart(2, '0')}` : ''}
                          </Text>
                        )}
                      </Flex>
                    );
                  })}
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
